import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Platform, Pressable, StyleSheet, TextInput, View } from 'react-native'
import { Button, Color, Font, Radius, Spacing } from 'zens-storybook'

interface ChatInputProps {
  inputText: string
  isLoading: boolean
  disabled?: boolean
  isInputFocused: boolean
  inputHeight: number
  onChangeText: (text: string) => void
  onSend: (text: string) => void
  onCancel: () => void
  onKeyPress: (e: any) => void
  onFocus: () => void
  onBlur: () => void
  onContentSizeChange: (height: number) => void
}

const ChatInput = forwardRef<TextInput, ChatInputProps>(({
  inputText,
  isLoading,
  disabled = false,
  isInputFocused,
  inputHeight,
  onChangeText,
  onSend,
  onCancel,
  onKeyPress,
  onFocus,
  onBlur,
  onContentSizeChange,
}, ref) => {
  const { t } = useTranslation()

  return (
    <View style={styles.inputContainer}>
      <View style={styles.inputBlock}>
        <TextInput
          ref={ref}
          style={[styles.input, isInputFocused && styles.inputFocused, { height: Math.min(Math.max(inputHeight, 40), 120), outlineStyle: 'none' } as any]}
          value={inputText}
          onChangeText={onChangeText}
          placeholder={t('Type your question...')}
          placeholderTextColor={Color.metalGrey}
          multiline
          onKeyPress={onKeyPress}
          editable={!isLoading && !disabled}
          // React Native Web maps editable=false to readOnly, which still accepts focus.
          {...(Platform.OS === 'web' ? { disabled } : {})}
          accessibilityState={{ disabled }}
          onFocus={onFocus}
          onBlur={onBlur}
          onContentSizeChange={(e) => onContentSizeChange(e.nativeEvent.contentSize.height)}
        />
        {isLoading ? (
          <Pressable
            onPress={onCancel}
            style={[styles.sendButton, { justifyContent: 'center', alignItems: 'center' }]}
            accessibilityRole="button"
            accessibilityLabel={t('Cancel request')}
          >
            <View style={{ width: 16, height: 16, backgroundColor: Color.premiumBlack, borderRadius: 2 }} />
          </Pressable>
        ) : (
          <Button style={styles.sendButton} onPress={() => onSend(inputText)} disabled={disabled || !inputText.trim()} right="send" />
        )}
      </View>
    </View>
  )
})

const styles = StyleSheet.create({
  inputContainer: {
    flexDirection: 'column',
    padding: Spacing.L,
    borderBottomLeftRadius: Radius.L,
    borderBottomRightRadius: Radius.L
  },
  inputBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Color.graphicGrey,
    padding: Spacing.XS,
    borderRadius: Radius.S,
    backgroundColor: Color.backgroundGrey,

  },
  input: {
    flex: 1,
    minHeight: 40,
    maxHeight: 120,
    borderColor: Color.graphicGrey,
    paddingHorizontal: Spacing.S,
    paddingVertical: Spacing.XS,
    marginRight: Spacing.S,
    backgroundColor: Color.backgroundGrey,
    lineHeight: 24,
    fontFamily: Font.light,
  },
  inputFocused: {
    borderColor: Color.simplyWhite,
  },
  sendButton: {
    width: 40,
    height: 40,
    padding: 0,
  },
})

export default ChatInput
